import { router } from '@inertiajs/react'
import { Plug } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Label } from '@/components/ui/Label'
import { CopyableText } from '@/components/ui/CopyableText'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type { McpConnectionRow } from '@/pages/Settings.types'

interface McpConnectionsCardProps {
  connections: McpConnectionRow[]
}

export function McpConnectionsCard({ connections }: McpConnectionsCardProps) {
  const { withAccount } = useRouteParams()
  const url = `${window.location.origin}/mcp`

  const disconnect = (connection: McpConnectionRow) => {
    router.delete(routes.mcpConnections.destroy.path(withAccount({ id: connection.id })), { preserveScroll: true })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Plug className="size-4" /> MCP clients</CardTitle>
        <CardDescription>
          Connect Claude or another AI client over MCP (Model Context Protocol) to work conversations and the
          help center as yourself, with your role in this workspace. It signs in through your browser, so
          there's no key to copy.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>MCP server URL</Label>
          <CopyableText text={url} className="font-mono text-xs break-all" />
        </div>
        <div className="space-y-2">
          <Label>Claude Code</Label>
          <CopyableText text={`claude mcp add banshee --transport http ${url}`} className="font-mono text-xs break-all" />
        </div>

        {connections.length > 0 && (
          <div className="rounded-md border border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Connected</TableHead>
                  <TableHead className="w-28" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.map(connection => (
                  <TableRow key={connection.id}>
                    <TableCell>
                      <div className="font-medium">{connection.name}</div>
                      <div className="text-xs text-muted-foreground break-all">{connection.redirect_hosts.join(', ')}</div>
                    </TableCell>
                    <TableCell>{new Date(connection.connected_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <ConfirmDialog
                        trigger={<Button variant="ghost" size="sm">Disconnect</Button>}
                        title={`Disconnect ${connection.name}?`}
                        description="It loses access to this workspace immediately. You can connect it again by signing in."
                        confirmText="Disconnect"
                        destructive
                        onConfirm={() => disconnect(connection)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
